import L from "leaflet";

/**
 * Returns Leaflet bounds from a GeoJSON object (FeatureCollection).
 * Handles Point, Polygon, MultiPolygon. GeoJSON uses [lng, lat].
 * @param {Object} geoJson - GeoJSON FeatureCollection or null
 * @returns {L.LatLngBounds|null}
 */
export function getBoundsFromGeoJson(geoJson) {
  if (!geoJson || !geoJson.features || !Array.isArray(geoJson.features) || geoJson.features.length === 0) {
    return null;
  }
  let bounds = null;
  for (let i = 0; i < geoJson.features.length; i++) {
    const feature = geoJson.features[i];
    if (!feature || !feature.geometry) continue;
    const coords = feature.geometry.coordinates;
    const type = feature.geometry.type;
    if (type === "Point" && coords && coords.length >= 2) {
      const lat = coords[1];
      const lng = coords[0];
      if (bounds == null) bounds = L.latLngBounds([lat, lng], [lat, lng]);
      else bounds.extend([lat, lng]);
    } else if (type === "Polygon" && coords && coords[0] && coords[0].length) {
      const ring = coords[0];
      for (let j = 0; j < ring.length; j++) {
        const lng = ring[j][0];
        const lat = ring[j][1];
        if (bounds == null) bounds = L.latLngBounds([lat, lng], [lat, lng]);
        else bounds.extend([lat, lng]);
      }
    } else if (type === "MultiPolygon" && coords && coords[0] && coords[0][0] && coords[0][0].length) {
      const ring = coords[0][0];
      for (let j = 0; j < ring.length; j++) {
        const lng = ring[j][0];
        const lat = ring[j][1];
        if (bounds == null) bounds = L.latLngBounds([lat, lng], [lat, lng]);
        else bounds.extend([lat, lng]);
      }
    }
  }
  return bounds;
}

/**
 * Returns Leaflet bounds from an array of points.
 * Each point: [lat, lng] or { lat, lng }.
 * @param {Array} points - Array of [lat, lng] or { lat, lng }
 * @returns {L.LatLngBounds|null}
 */
export function getBoundsFromPointArray(points) {
  if (!points || !Array.isArray(points) || points.length === 0) return null;
  let bounds = null;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    let lat, lng;
    if (Array.isArray(p)) {
      lat = p[0];
      lng = p[1];
    } else if (p && typeof p.lat === "number" && typeof p.lng === "number") {
      lat = p.lat;
      lng = p.lng;
    } else {
      continue;
    }
    if (bounds == null) bounds = L.latLngBounds([lat, lng], [lat, lng]);
    else bounds.extend([lat, lng]);
  }
  return bounds;
}

/**
 * Merges two Leaflet bounds. Returns null only if both are null.
 * @param {L.LatLngBounds|null} boundsA
 * @param {L.LatLngBounds|null} boundsB
 * @returns {L.LatLngBounds|null}
 */
export function mergeBounds(boundsA, boundsB) {
  if (boundsA == null && boundsB == null) return null;
  if (boundsA == null) return boundsB;
  if (boundsB == null) return boundsA;
  const merged = L.latLngBounds(boundsA.getSouthWest(), boundsA.getNorthEast());
  merged.extend(boundsB);
  return merged;
}

/**
 * Returns combined Leaflet bounds for all map elements: main GeoJSON, drawn polygons, and drawn point arrays.
 * Uses state.map slice. Does not include copy-mode overlays.
 * @param {Object} state - Full Redux state (reads state.map)
 * @returns {L.LatLngBounds|null}
 */
export function getCombinedMapBounds(state) {
  const mapState = state && state.map;
  if (!mapState) return null;

  const mainBounds = getBoundsFromGeoJson(mapState.geoJsonWg);
  const drawnPolygonBounds = getBoundsFromGeoJson(mapState.drawnPolygonGeojsonWg);
  const treesBounds = getBoundsFromPointArray(mapState.allDrawnTreesCoordinatesWg);
  const emissionsBounds = getBoundsFromPointArray(mapState.allDrawnEmissionCoordinatesWg);
  const poisBounds = getBoundsFromPointArray(mapState.allDrawnMapPoiCoordinatesWg);
  const windTurbinesBounds = getBoundsFromPointArray(mapState.allDrawnWindTurbineCoordinatesWg);

  let combined = mainBounds;
  combined = mergeBounds(combined, drawnPolygonBounds);
  combined = mergeBounds(combined, treesBounds);
  combined = mergeBounds(combined, emissionsBounds);
  combined = mergeBounds(combined, poisBounds);
  combined = mergeBounds(combined, windTurbinesBounds);

  return combined;
}
